import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { subsApi, settingsApi } from "@/lib/api";
import { toast } from "sonner";

const SubsContext = createContext(null);

export function SubsProvider({ children }) {
  const [subs, setSubs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const [list, s] = await Promise.all([subsApi.list(), settingsApi.get()]);
    setSubs(list);
    setSettings(s);
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const list = await subsApi.list();
        if (list.length === 0) {
          await subsApi.seedDemo();
        }
        await refresh();
      } catch (e) {
        console.error(e);
        toast.error("Impossible de charger les données");
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [refresh]);

  const addSub = async (payload) => {
    const created = await subsApi.create(payload);
    setSubs((prev) => [...prev, created]);
    toast.success(`Abonnement « ${created.name} » ajouté`);
    return created;
  };

  const updateSub = async (id, payload) => {
    const updated = await subsApi.update(id, payload);
    setSubs((prev) => prev.map((s) => (s.id === id ? updated : s)));
    toast.success("Abonnement modifié");
    return updated;
  };

  const removeSub = async (id) => {
    const target = subs.find((s) => s.id === id);
    await subsApi.remove(id);
    setSubs((prev) => prev.filter((s) => s.id !== id));
    toast.success(`« ${target?.name || "Abonnement"} » supprimé`);
  };

  const clearDemo = async () => {
    const res = await subsApi.removeAllDemo();
    await refresh();
    toast.success(`${res.deleted} abonnement(s) de démo supprimé(s)`);
  };

  const saveSettings = async (payload) => {
    const updated = await settingsApi.update(payload);
    setSettings(updated);
    toast.success("Paramètres enregistrés");
  };

  return (
    <SubsContext.Provider
      value={{ subs, settings, loading, addSub, updateSub, removeSub, clearDemo, saveSettings, refresh }}
    >
      {children}
    </SubsContext.Provider>
  );
}

export function useSubs() {
  const ctx = useContext(SubsContext);
  if (!ctx) throw new Error("useSubs must be used within SubsProvider");
  return ctx;
}
